import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import axios from 'axios';

const { FiUsers, FiUserPlus, FiEdit, FiSave, FiX, FiShield, FiUserCheck, FiClipboard, FiPhone, FiMail } = FiIcons;

const UserSettings = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'receptionist',
    contact: ''
  });

  useEffect(() => {
    fetchUsers();
    fetchDoctors();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/settings/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchDoctors = async () => {
    try {
      const response = await axios.get('/api/doctors');
      setDoctors(response.data);
    } catch (error) {
      console.error('Error fetching doctors:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const openAddModal = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'receptionist',
      contact: ''
    });
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '', // Password field is empty when editing
      role: user.role,
      contact: user.contact || ''
    });
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (editingUser) {
        // Update existing user
        await axios.put(`/api/settings/users/${editingUser.id}`, formData);
        setSuccess('User updated successfully');
      } else {
        // Create new user
        await axios.post('/api/settings/users', formData);
        setSuccess('User created successfully');
      }

      // Refresh user list
      fetchUsers();
      fetchDoctors();

      // Close modal after a short delay
      setTimeout(() => {
        setShowModal(false);
      }, 1500);
    } catch (error) {
      console.error('Error saving user:', error);
      setError(error.response?.data?.error || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'manager': return 'bg-blue-100 text-blue-800';
      case 'doctor': return 'bg-green-100 text-green-800';
      case 'receptionist': return 'bg-purple-100 text-purple-800';
      default: return 'bg-grey-100 text-grey-800';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return FiShield;
      case 'manager': return FiClipboard;
      case 'doctor': return FiUserCheck;
      case 'receptionist': return FiUsers;
      default: return FiUsers;
    }
  };

  // Get doctor profile image if available
  const getDoctorProfileImage = (userId) => {
    if (!doctors || doctors.length === 0) return null;
    const doctorData = doctors.find(d => d.id === userId);
    return doctorData?.profile_image || null;
  };

  // Get user initials for avatar
  const getUserInitials = (name) => {
    if (!name) return "";
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-maroon-600"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-grey-900">User Management</h2>
            <p className="mt-1 text-sm text-grey-600">
              Add and manage system users with different roles and permissions.
            </p>
          </div>
          <button
            onClick={openAddModal}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-maroon-600 hover:bg-maroon-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-maroon-500"
          >
            <SafeIcon icon={FiUserPlus} className="w-4 h-4 mr-2" />
            Add New User
          </button>
        </div>

        {/* User List */}
        <div className="bg-white shadow overflow-hidden rounded-md">
          <ul className="divide-y divide-grey-200">
            {users.length === 0 ? (
              <li className="px-6 py-12 text-center">
                <SafeIcon icon={FiUsers} className="mx-auto h-12 w-12 text-grey-400" />
                <h3 className="mt-2 text-sm font-medium text-grey-900">No users found</h3>
                <p className="mt-1 text-sm text-grey-500">
                  Get started by adding a new user.
                </p>
              </li>
            ) : (
              users.map((user) => {
                const profileImage = user.role === 'doctor' ? getDoctorProfileImage(user.id) : null;
                return (
                  <li key={user.id} className="px-6 py-5 hover:bg-grey-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {profileImage ? (
                            <img
                              src={profileImage}
                              alt={user.name}
                              className="h-10 w-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className={`h-10 w-10 rounded-full ${user.role === 'doctor' ? 'bg-green-100' : 'bg-maroon-100'} flex items-center justify-center`}>
                              <SafeIcon
                                icon={getRoleIcon(user.role)}
                                className={`h-5 w-5 ${user.role === 'doctor' ? 'text-green-600' : 'text-maroon-600'}`}
                              />
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="font-medium text-grey-900">{user.name}</div>
                          <div className="text-sm text-grey-500 flex items-center space-x-2">
                            <span className="flex items-center">
                              <SafeIcon icon={FiMail} className="w-3 h-3 mr-1" />
                              {user.email}
                            </span>
                            {user.contact && (
                              <span className="flex items-center">
                                <SafeIcon icon={FiPhone} className="w-3 h-3 mr-1" />
                                {user.contact}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                          {user.role}
                        </span>
                        <button
                          onClick={() => openEditModal(user)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-maroon-700 bg-maroon-100 hover:bg-maroon-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-maroon-500"
                        >
                          <SafeIcon icon={FiEdit} className="w-4 h-4 mr-1" />
                          Edit
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })
            )}
          </ul>
        </div>

        {/* Add/Edit User Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-grey-900">
                  {editingUser ? 'Edit User' : 'Add New User'}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-grey-400 hover:text-grey-500"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>

              {error && (
                <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4 rounded">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <SafeIcon icon={FiIcons.FiAlertTriangle} className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {success && (
                <div className="mb-4 bg-green-50 border-l-4 border-green-500 p-4 rounded">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <SafeIcon icon={FiIcons.FiCheckCircle} className="h-5 w-5 text-green-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-green-700">{success}</p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-grey-700">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="mt-1 block w-full border-grey-300 rounded-md shadow-sm focus:ring-maroon-500 focus:border-maroon-500 sm:text-sm"
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-grey-700">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="mt-1 block w-full border-grey-300 rounded-md shadow-sm focus:ring-maroon-500 focus:border-maroon-500 sm:text-sm"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-grey-700">
                    Password {editingUser && '(Leave blank to keep current password)'}
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    required={!editingUser}
                    className="mt-1 block w-full border-grey-300 rounded-md shadow-sm focus:ring-maroon-500 focus:border-maroon-500 sm:text-sm"
                    value={formData.password}
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-grey-700">
                    Role
                  </label>
                  <select
                    id="role"
                    name="role"
                    required
                    className="mt-1 block w-full border-grey-300 rounded-md shadow-sm focus:ring-maroon-500 focus:border-maroon-500 sm:text-sm"
                    value={formData.role}
                    onChange={handleInputChange}
                  >
                    <option value="admin">Admin</option>
                    <option value="manager">Manager</option>
                    <option value="doctor">Doctor</option>
                    <option value="receptionist">Receptionist</option>
                  </select>
                  {formData.role === 'sysadmin' && (
                    <p className="mt-1 text-xs text-red-600">
                      Note: Sysadmin role cannot be created through this interface
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="contact" className="block text-sm font-medium text-grey-700">
                    Contact Number
                  </label>
                  <input
                    type="text"
                    id="contact"
                    name="contact"
                    className="mt-1 block w-full border-grey-300 rounded-md shadow-sm focus:ring-maroon-500 focus:border-maroon-500 sm:text-sm"
                    value={formData.contact}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="pt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="mr-3 px-4 py-2 border border-grey-300 shadow-sm text-sm font-medium rounded-md text-grey-700 bg-white hover:bg-grey-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-maroon-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-maroon-600 hover:bg-maroon-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-maroon-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                        {editingUser ? 'Update User' : 'Create User'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default UserSettings;